import './routing-panel.component.scss';

import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, Subject, of } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { InstructionType } from '@navvis/ivion';
import * as THREE from 'three';

import { TranslatePipe } from '../pipes/translate.pipe';
import { TranslationService } from '../services/translation.service';
import { MiddlewareApiService, SearchResult, SearchResultKind } from '../api/middleware-api.service';
import { SiteService } from '../site-selector/site.service';
import { RouteService, SecurityZone } from './route.service';
import { RouteEventService } from './route-event.service';
import { RouteDefinition, RouteInstructionVm, RoutePointType } from './routing.models';
import { RouteInstructionComponent } from './route-instruction.component';
import { RouteWalkInstructionComponent } from './route-walk-instruction.component';
import { InaccessibleRouteDialogComponent } from './inaccessible-route-dialog.component';
import { InaccurateNavigationDialogComponent } from './inaccurate-navigation-dialog.component';

@Component({
  selector: 'app-routing-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    RouteInstructionComponent,
    RouteWalkInstructionComponent,
    InaccessibleRouteDialogComponent,
    InaccurateNavigationDialogComponent,
  ],
  template: `
    <div class="route-search">
      <div class="route-search-container">
        <form (submit)="$event.preventDefault()">
          <div class="route-search-inputs">
            <div class="route-search-inputs-from">
              <input
                type="search"
                autocomplete="off"
                [placeholder]="'navigation_input_start_placeholder' | translate"
                [(ngModel)]="routeFromName"
                name="routeStart"
                [readonly]="routeToBusy"
                (ngModelChange)="onFromInputChanged($event)"
                (focus)="inputFocus(0)"
              />
              <img
                src="assets/images/tail-spin.svg"
                class="route-search-inputs-loading"
                *ngIf="routeFromBusy"
              />
              <img
                src="assets/images/icn_clear_input_grey.svg"
                *ngIf="!routeFromBusy && routeFromName.length > 0"
                (click)="clearRouteFrom()"
              />
            </div>
            <hr />
            <div class="route-search-inputs-to">
              <input
                type="search"
                autocomplete="off"
                [placeholder]="'navigation_input_end_placeholder' | translate"
                [(ngModel)]="routeToName"
                name="routeEnd"
                [readonly]="routeFromBusy"
                (ngModelChange)="onToInputChanged($event)"
                (focus)="inputFocus(1)"
              />
              <img
                src="assets/images/tail-spin.svg"
                class="route-search-inputs-loading"
                *ngIf="routeToBusy"
              />
              <img
                src="assets/images/icn_clear_input_grey.svg"
                *ngIf="!routeToBusy && routeToName.length > 0"
                (click)="clearRouteTo()"
              />
            </div>
          </div>
          <div class="route-search-switch">
            <button type="button" (click)="switchRoute()">
              <img src="assets/images/swap_grey.svg" />
            </button>
          </div>
          <div class="route-search-close">
            <button type="button" (click)="clearAndClose()">
              <img src="assets/images/close_grey.svg" />
            </button>
          </div>
        </form>
        <div class="route-no-route-msg" *ngIf="showNoRouteMsg">
          {{ 'navigation_no_route_message' | translate }}
        </div>
      </div>

      <div class="route-search-results" *ngIf="searchResults.length > 0">
        <div class="route-search-results-title">
          {{ 'navigation_search_results_label' | translate }} "{{ currentSearchTerm }}"
        </div>
        <ul>
          <li *ngFor="let item of searchResults" (click)="selectRoutePoint(item)">
            <div class="clickable">
              <h3>
                <img [src]="item.sourceKind === 0 ? 'assets/images/icon-building_64x64.png' : 'assets/images/icon-functional-area_64x64.png'" />
                {{ item.title }}
              </h3>
              <p>{{ item.description }}</p>
            </div>
          </li>
        </ul>
      </div>

      <div class="route-search-instructions" *ngIf="instructionVms.length > 0">
        <div class="route-search-instructions-length">
          {{ 'navigation_route_length_label' | translate }}: {{ routeLength }} m ({{ routeDuration }} min)
        </div>
        <div class="route-search-instructions-warning" *ngIf="inaccessibleZones.length > 0">
          <div class="route-search-instructions-warning-left">
            <img src="assets/images/warning.png" />
          </div>
          <div class="route-search-instructions-warning-right">
            <div>{{ 'navigation_inaccessible_zones_warning_msg_1' | translate }}</div>
            <div class="security-zones-title">{{ 'navigation_inaccessible_zones_warning_msg_2' | translate }}</div>
            <span *ngFor="let zone of inaccessibleZones; let i = index" class="security-zone-text">
              {{ zone.code }}: {{ zone.name }}<span *ngIf="i < inaccessibleZones.length - 1">, </span>
            </span>
          </div>
        </div>
        <ul>
          <li *ngFor="let item of instructionVms">
            <app-route-instruction
              *ngIf="item.type !== 'edge'"
              [instruction]="item">
            </app-route-instruction>
            <app-route-walk-instruction
              *ngIf="item.type === 'edge'"
              [instruction]="item">
            </app-route-walk-instruction>
          </li>
        </ul>
      </div>

      <app-inaccessible-route-dialog
        *ngIf="showInaccessibleDialog"
        [zones]="inaccessibleZones"
        (confirmed)="onInaccessibleConfirmed()">
      </app-inaccessible-route-dialog>

      <app-inaccurate-navigation-dialog
        *ngIf="showInaccurateDialog"
        (confirmed)="onInaccurateConfirmed()"
        (cancelled)="onInaccurateCancelled()">
      </app-inaccurate-navigation-dialog>
    </div>
  `,
  styles: [],
})
export class RoutingPanelComponent implements OnInit, OnDestroy {
  @Output() closeEvent = new EventEmitter<void>();

  routeFromName = '';
  routeToName = '';
  routeFromBusy = false;
  routeToBusy = false;
  activeRoutePoint: RoutePointType = RoutePointType.From;
  searchResults: SearchResult[] = [];
  currentSearchTerm = '';
  instructionVms: RouteInstructionVm[] = [];
  showNoRouteMsg = false;
  routeLength = 0;
  routeDuration = 0;
  inaccessibleZones: SecurityZone[] = [];
  showInaccessibleDialog = false;
  showInaccurateDialog = false;

  private routeFrom: RouteDefinition | null = null;
  private routeTo: RouteDefinition | null = null;
  private pendingPoiId: number | null = null;
  private pendingItemName: string | null = null;

  private fromSearchSubject = new Subject<string>();
  private toSearchSubject = new Subject<string>();
  private routeEventSubscription: Subscription | null = null;
  private fromSearchSubscription: Subscription | null = null;
  private toSearchSubscription: Subscription | null = null;

  private cdr = inject(ChangeDetectorRef);
  private translationService = inject(TranslationService);
  private apiService = inject(MiddlewareApiService);
  private siteService = inject(SiteService);
  private routeService = inject(RouteService);
  private routeEventService = inject(RouteEventService);

  private get ivApi(): any {
    return (window as any).IvionApp?.ivApi;
  }

  ngOnInit(): void {
    this.routeEventSubscription = this.routeEventService.routeEvent$.subscribe(event => {
      const defaultName = event.routeType === RoutePointType.From
        ? this.translationService.translate('navigation_route_start_title')
        : this.translationService.translate('navigation_route_end_title');

      if (event.routeType === RoutePointType.From) {
        this.routeFrom = { type: RoutePointType.From, position: event.position, name: defaultName, isPoi: false };
        this.routeFromName = defaultName;
      } else {
        this.routeTo = { type: RoutePointType.To, position: event.position, name: defaultName, isPoi: false };
        this.routeToName = defaultName;
      }

      if (this.routeFrom && this.routeTo) {
        this.searchRoute();
      }
      this.cdr.detectChanges();
    });

    this.fromSearchSubscription = this.fromSearchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(term => this.performSearch(term));

    this.toSearchSubscription = this.toSearchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(term => this.performSearch(term));
  }

  ngOnDestroy(): void {
    this.routeEventSubscription?.unsubscribe();
    this.fromSearchSubscription?.unsubscribe();
    this.toSearchSubscription?.unsubscribe();
    this.fromSearchSubject.complete();
    this.toSearchSubject.complete();
    this.clearIvionRoute();
  }

  onFromInputChanged(value: string): void {
    this.activeRoutePoint = RoutePointType.From;
    if (!value || value.length < 2) {
      this.searchResults = [];
      return;
    }
    this.fromSearchSubject.next(value);
  }

  onToInputChanged(value: string): void {
    this.activeRoutePoint = RoutePointType.To;
    if (!value || value.length < 2) {
      this.searchResults = [];
      return;
    }
    this.toSearchSubject.next(value);
  }

  private performSearch(term: string): void {
    if (!term || term.length < 2) {
      this.searchResults = [];
      return;
    }

    this.instructionVms = [];
    this.currentSearchTerm = term;

    const siteId = this.siteService.selectedSite?.id;
    if (!siteId) return;

    this.setFmsSearchBusy(true);

    this.apiService.searchPois(siteId, term).subscribe({
      next: (results) => {
        this.searchResults = results.filter(r => r.isInNavvisMap);
        this.setFmsSearchBusy(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.setFmsSearchBusy(false);
        this.cdr.detectChanges();
      },
    });
  }

  inputFocus(routeType: number): void {
    this.activeRoutePoint = routeType as RoutePointType;
    this.searchResults = [];
  }

  selectRoutePoint(item: SearchResult): void {
    this.searchResults = [];

    const siteId = this.siteService.selectedSite?.id;
    if (!siteId) return;

    this.setFmsSearchBusy(true);

    let obs;
    if (item.sourceKind === SearchResultKind.Building) {
      obs = this.apiService.buildingToPoi(siteId, item.sourceId);
    } else if (item.sourceKind === SearchResultKind.StaticPoi) {
      obs = of({ found: true, id: item.sourceId, isBuildingFallback: false });
    } else {
      obs = this.apiService.functionalAreaToPoi(siteId, item.sourceId);
    }

    obs.subscribe({
      next: async (result) => {
        this.setFmsSearchBusy(false);

        if (!result.found) {
          this.cdr.detectChanges();
          return;
        }

        if (result.isBuildingFallback) {
          this.pendingPoiId = result.id;
          this.pendingItemName = item.title;
          this.showInaccurateDialog = true;
          this.cdr.detectChanges();
          return;
        }

        await this.resolvePoiAndSetRoute(result.id, item.title);
      },
      error: (e) => {
        console.error('[RoutingPanel] Error resolving search result to POI:', e);
        this.setFmsSearchBusy(false);
        this.cdr.detectChanges();
      },
    });
  }

  async onInaccurateConfirmed(): Promise<void> {
    this.showInaccurateDialog = false;
    if (this.pendingPoiId !== null && this.pendingItemName !== null) {
      await this.resolvePoiAndSetRoute(this.pendingPoiId, this.pendingItemName);
    }
    this.pendingPoiId = null;
    this.pendingItemName = null;
  }

  onInaccurateCancelled(): void {
    this.showInaccurateDialog = false;
    this.pendingPoiId = null;
    this.pendingItemName = null;
    this.setFmsSearchBusy(false);
    this.cdr.detectChanges();
  }

  onInaccessibleConfirmed(): void {
    this.showInaccessibleDialog = false;
    this.cdr.detectChanges();
  }

  switchRoute(): void {
    if (!this.routeFrom && !this.routeTo) return;

    const fromCopy = this.routeFrom ? { ...this.routeFrom } : null;
    const toCopy = this.routeTo ? { ...this.routeTo } : null;

    this.routeFrom = toCopy ? { ...toCopy, type: RoutePointType.From } : null;
    this.routeTo = fromCopy ? { ...fromCopy, type: RoutePointType.To } : null;

    this.routeFromName = this.routeFrom?.name ?? '';
    this.routeToName = this.routeTo?.name ?? '';

    if (this.routeFrom && this.routeTo) {
      this.searchRoute();
    } else {
      this.clearRouteData();
    }
  }

  clearRouteFrom(): void {
    this.routeFrom = null;
    this.routeFromName = '';
    this.clearRouteData();
  }

  clearRouteTo(): void {
    this.routeTo = null;
    this.routeToName = '';
    this.clearRouteData();
  }

  clearAndClose(): void {
    this.routeFrom = null;
    this.routeTo = null;
    this.routeFromName = '';
    this.routeToName = '';
    this.clearRouteData();
    this.closeEvent.emit();
  }

  searchRoute(): void {
    this.showNoRouteMsg = false;
    this.instructionVms = [];

    if (!this.routeFrom?.position || !this.routeTo?.position) return;

    const from = new THREE.Vector3(this.routeFrom.position.x, this.routeFrom.position.y, this.routeFrom.position.z);
    const to = new THREE.Vector3(this.routeTo.position.x, this.routeTo.position.y, this.routeTo.position.z);

    this.ivApi?.routing?.route(from, to, false)
      .then((routeData: any) => {
        if (!routeData.available) {
          this.showNoRouteMsg = true;
          this.cdr.detectChanges();
          return Promise.resolve();
        }

        const duration = Math.round(routeData.routeLength * 0.75 / 60);
        this.routeLength = Math.round(routeData.routeLength);
        this.routeDuration = duration > 0 ? duration : 1;

        return Promise.all([
          this.prepareInstructions(routeData.instructions),
          this.checkSecurityZones(),
        ]);
      })
      .catch(() => {
        this.showNoRouteMsg = true;
        this.cdr.detectChanges();
      });
  }

  private clearRouteData(): void {
    this.showNoRouteMsg = false;
    this.searchResults = [];
    this.instructionVms = [];
    this.inaccessibleZones = [];
    this.clearIvionRoute();
    this.cdr.detectChanges();
  }

  private clearIvionRoute(): void {
    try {
      this.ivApi?.routing?.clear();
    } catch (e) {
      // ignore
    }
  }

  private async resolvePoiAndSetRoute(poiId: number, name: string): Promise<void> {
    try {
      const poi = await this.ivApi.poi.repository.findOne(poiId);
      this.setRelatedRoutePoint(name, poi.location);
    } catch (e) {
      console.error('[RoutingPanel] Error resolving POI location:', e);
    }
  }

  private setRelatedRoutePoint(name: string, position: { x: number; y: number; z: number }): void {
    if (this.activeRoutePoint === RoutePointType.To) {
      this.routeTo = { isPoi: true, position, name, type: RoutePointType.To };
      this.routeToName = name;
    } else {
      this.routeFrom = { isPoi: true, position, name, type: RoutePointType.From };
      this.routeFromName = name;
    }
    this.searchRoute();
    this.cdr.detectChanges();
  }

  private async prepareInstructions(instructions: any[]): Promise<void> {
    const siteId = this.siteService.selectedSite?.id;

    const instructionVms: RouteInstructionVm[] = instructions.map(instr => ({
      originalInstruction: instr,
      type: instr.type as string,
      floor: (instr.floor?.name as string | null) ?? null,
      buildingName: null as string | null,
      position: { lon: instr.position.x, lat: instr.position.y, z: instr.position.z },
      name: null as string | null,
    }));

    const containsBuildingEntry = instructions.some(i => i.type === InstructionType.ENTER_BUILDING);

    let itemsToMap: RouteInstructionVm[];
    if (!containsBuildingEntry) {
      itemsToMap = instructionVms.filter(i =>
        i.type !== InstructionType.EDGE &&
        !i.type.includes('floor') &&
        !i.type.includes('stairs') &&
        !i.type.includes('elevator')
      );
    } else {
      itemsToMap = instructionVms.filter(i => i.type !== InstructionType.EDGE);
    }

    if (siteId && itemsToMap.length > 0) {
      try {
        const locations = itemsToMap.map(i => i.position);
        const buildingNames = await this.apiService.getBuildingNamesByLocations(siteId, locations).toPromise();
        if (buildingNames) {
          instructionVms.forEach(item => {
            const key = `${item.position.lon}-${item.position.lat}-${item.position.z}`;
            const buildingName = buildingNames[key];
            if (buildingName) {
              item.buildingName = buildingName;
            }
          });
        }
        this.processBuildingEntryInstructions(instructionVms);
      } catch (e) {
        console.error('[RoutingPanel] Error getting building names:', e);
      }
    }

    this.instructionVms = instructionVms;
    this.cdr.detectChanges();
  }

  private processBuildingEntryInstructions(instructions: RouteInstructionVm[]): void {
    const buildingEntries = instructions.filter(
      i => i.type === InstructionType.ENTER_BUILDING && i.buildingName === null
    );

    buildingEntries.forEach(be => {
      const index = instructions.indexOf(be);
      const nearestBuilding = instructions.find((i, idx) => idx > index && i.buildingName !== null);
      if (nearestBuilding) {
        be.buildingName = nearestBuilding.buildingName;
      }
    });
  }

  private checkSecurityZones(): void {
    if (!this.routeFrom?.position || !this.routeTo?.position) return;

    this.routeService.getRouteSecurityZones({
      routeFrom: {
        lon: this.routeFrom.position.x,
        lat: this.routeFrom.position.y,
        z: this.routeFrom.position.z,
      },
      routeTo: {
        lon: this.routeTo.position.x,
        lat: this.routeTo.position.y,
        z: this.routeTo.position.z,
      },
    }).subscribe({
      next: (result) => {
        const inaccessible: SecurityZone[] = [];

        if (result.startSecurityZone && !result.startSecurityZone.isAccessible) {
          inaccessible.push(result.startSecurityZone);
        }
        if (result.otherSecurityZones) {
          result.otherSecurityZones.filter(z => !z.isAccessible).forEach(z => inaccessible.push(z));
        }
        if (result.destinationSecurityZone && !result.destinationSecurityZone.isAccessible) {
          inaccessible.push(result.destinationSecurityZone);
        }

        this.inaccessibleZones = inaccessible.filter(
          (z, i, arr) => arr.map(p => p.code).indexOf(z.code) === i
        );

        if (this.inaccessibleZones.length > 0) {
          this.showInaccessibleDialog = true;
        }
        this.cdr.detectChanges();
      },
      error: (e) => {
        console.error('[RoutingPanel] Security zones check failed:', e);
      },
    });
  }

  private setFmsSearchBusy(busy: boolean): void {
    if (this.activeRoutePoint === RoutePointType.From) {
      this.routeFromBusy = busy;
    } else {
      this.routeToBusy = busy;
    }
    this.cdr.detectChanges();
  }
}
